import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { Loader2, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

/**
 * /auth/callback
 *
 * Handler for Supabase magic-link / OTP / OAuth redirects.
 *
 * Supabase can return the auth payload in two ways depending on the project's
 * configured flow:
 *
 *   1. PKCE flow → ?code=<code>          (newer projects / OAuth)
 *   2. Implicit flow → #access_token=...&refresh_token=...&type=magiclink
 *
 * In BOTH cases we must:
 *   - Exchange / read the tokens from the URL
 *   - Establish the Supabase session
 *   - Strip the tokens out of the URL (so users never see raw token text)
 *   - Redirect into the app (or show a friendly "expired link" error)
 *
 * The Supabase JS client auto-detects the implicit-flow hash on load
 * (because we use the default `detectSessionInUrl: true`). For PKCE codes
 * we explicitly call `exchangeCodeForSession`. Either way, by the time the
 * user sees anything we either have a session (→ redirect) or a clear
 * error message — never a raw token blob.
 */
const AuthCallback: React.FC = () => {
  const navigate = useNavigate();
  const ranRef = useRef(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const finish = async () => {
      try {
        const url = new URL(window.location.href);
        const hash = window.location.hash || '';
        const hashParams = new URLSearchParams(
          hash.startsWith('#') ? hash.slice(1) : hash
        );

        // ── 1. Surface explicit error params from Supabase ────────────────
        // Supabase appends ?error=... or #error=... when a link is expired,
        // already-used, or otherwise invalid. We translate those into a
        // friendly message instead of leaving the raw text on screen.
        const errParam =
          url.searchParams.get('error') ||
          url.searchParams.get('error_code') ||
          hashParams.get('error') ||
          hashParams.get('error_code');
        const errDesc =
          url.searchParams.get('error_description') ||
          hashParams.get('error_description');

        if (errParam) {
          const combined = `${errParam} ${errDesc ?? ''}`.toLowerCase();
          if (
            combined.includes('expired') ||
            combined.includes('invalid') ||
            combined.includes('otp')
          ) {
            setErrorMsg('Sign-in link expired. Please request a new one.');
          } else {
            setErrorMsg(
              errDesc
                ? decodeURIComponent(errDesc.replace(/\+/g, ' '))
                : 'Sign-in link could not be used. Please request a new one.'
            );
          }
          // Wipe sensitive params from address bar.
          window.history.replaceState({}, document.title, '/auth/callback');
          return;
        }

        // ── 2. PKCE flow: exchange ?code=... for a session ────────────────
        const code = url.searchParams.get('code');
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(
            window.location.href
          );
          if (error) {
            console.error('[AuthCallback] exchangeCodeForSession failed', error);
            setErrorMsg('Sign-in link expired. Please request a new one.');
            window.history.replaceState({}, document.title, '/auth/callback');
            return;
          }
        }

        // ── 3. Implicit flow: tokens live in the URL hash. ────────────────
        // The supabase-js client auto-detects them on load, but in case the
        // module loaded before the hash was present we set them explicitly.
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        if (!code && accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            console.error('[AuthCallback] setSession failed', error);
            setErrorMsg('Sign-in link expired. Please request a new one.');
            window.history.replaceState({}, document.title, '/auth/callback');
            return;
          }
        }

        // ── 4. Strip tokens from the URL BEFORE doing anything else ───────
        // so users never see access_token / refresh_token text in the bar.
        window.history.replaceState({}, document.title, '/auth/callback');

        // ── 5. Confirm we now have a session and route into the app ──────
        const { data, error: sessErr } = await supabase.auth.getSession();
        if (sessErr || !data.session) {
          setErrorMsg('Your sign-in link has expired. Please request a new one.');
          return;
        }

        const userId = data.session.user.id;

        // ── 6. New user vs existing user ─────────────────────────────────
        // We treat a user as "new" if they have no profile row yet, or if
        // their profile has not been set up (no family_name). Brand-new
        // accounts go to /join to complete onboarding; returning users
        // land on their dashboard / family tree at "/".
        let isNewUser = false;
        try {
          const { data: prof, error: profErr } = await supabase
            .from('profiles')
            .select('id, family_name, name')
            .eq('id', userId)
            .maybeSingle();
          if (profErr) {
            console.warn('[AuthCallback] profile lookup failed', profErr);
          }
          if (!prof) {
            isNewUser = true;
          } else if (!prof.family_name && !prof.name) {
            isNewUser = true;
          }
        } catch (lookupErr) {
          console.warn('[AuthCallback] profile lookup exception', lookupErr);
        }

        navigate(isNewUser ? '/signup' : '/app', { replace: true });
      } catch (err) {
        console.error('[AuthCallback] unexpected error', err);
        setErrorMsg('Your sign-in link has expired. Please request a new one.');
        // Still scrub the URL just in case.
        try {
          window.history.replaceState({}, document.title, '/auth/callback');
        } catch {
          /* noop */
        }
      }
    };

    finish();
  }, [navigate]);


  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-fuchsia-50 via-white to-pink-50 px-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-xl border border-fuchsia-100 overflow-hidden">
        <div className="bg-gradient-to-br from-fuchsia-700 via-fuchsia-600 to-pink-600 text-white p-6 flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-white/15 flex items-center justify-center ring-2 ring-white/30">
            <img
              src="/brand/kinscircle-tree-icon.png"
              alt="KinsCircle"
              className="w-full h-full rounded-full object-cover"
            />
          </div>
          <div>
            <h1 className="font-serif text-xl font-bold leading-tight">
              KinsCircle
            </h1>
            <p className="text-white/80 text-xs">
              {errorMsg ? 'Sign-in link issue' : 'Signing you in…'}
            </p>
          </div>
        </div>

        <div className="p-6">
          {!errorMsg ? (
            <div className="flex flex-col items-center text-center py-6">
              <Loader2 className="w-8 h-8 text-fuchsia-600 animate-spin mb-3" />
              <p className="text-slate-700 font-medium">
                Completing your sign-in…
              </p>
              <p className="text-slate-500 text-sm mt-1">
                You'll be taken to your family circle in a moment.
              </p>
            </div>
          ) : (
            <div className="text-center py-2">
              <div className="mx-auto w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mb-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <p className="text-slate-800 font-semibold">{errorMsg}</p>
              <p className="text-slate-500 text-sm mt-1">
                Magic links are single-use and expire after a short time for
                your security.
              </p>
              <div className="mt-5 flex flex-col gap-2">
                <Button
                  onClick={() => navigate('/login', { replace: true })}
                  className="w-full rounded-full bg-gradient-to-r from-fuchsia-600 to-pink-500 hover:from-fuchsia-700 hover:to-pink-600 text-white"
                >
                  Back to KinsCircle
                </Button>
                <p className="text-xs text-slate-500">
                  From the home page, tap{' '}
                  <span className="font-semibold">Sign in</span> →{' '}
                  <span className="font-semibold">Sign in with Email Link</span>{' '}
                  to request a fresh link.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
